import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { SiteLayout } from "@/features/marketing/SiteLayout";
import { useDocumentMeta } from "@/lib/document-meta";

const BUSINESS_FEATURES = [
  "The business apps: Accounting first, with inventory, purchasing and more as they ship",
  "Organizations — one working environment per company, client, or engagement",
  "Extensions and Developer access",
  "Business extensions and the costing close review workflow",
];

const APPS_FEATURES = [
  "SYLVIQ Passwords",
  "SYLVIQ Authenticator",
  "Every personal app added to the Apps area",
];

export function PricingPage() {
  useDocumentMeta({
    title: "Pricing — Business and personal apps | SYLVIQ",
    description:
      "SYLVIQ personal apps are free. Business adds the business apps and organization-management surface; cloud and the AI platform are separate products with their own pricing.",
    path: "/pricing",
  });
  return (
    <SiteLayout>
      <section className="mx-auto w-full max-w-6xl px-4 pt-16 pb-8 sm:px-6 sm:pt-20">
        <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
          Pricing
        </p>
        <h1 className="mt-3 max-w-3xl text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
          Personal apps free. Business when you need it
        </h1>
        <p className="mt-4 max-w-2xl text-base text-pretty text-[var(--muted-foreground)] sm:text-lg">
          The Apps area is free for everyone: your personal apps, no organization or
          team setup required. Business is a paid plan that opens the business
          apps and the organization-management surface.
        </p>
      </section>

      <section className="mx-auto grid w-full max-w-6xl gap-6 px-4 pb-20 sm:px-6 lg:grid-cols-2">
        <article className="surface flex flex-col rounded-2xl p-6">
          <h2 className="text-lg font-semibold tracking-tight">Apps</h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Free forever. A simple personal launcher — no business features, no
            organization setup.
          </p>
          <ul className="mt-5 grid gap-3 text-sm">
            {APPS_FEATURES.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <Check
                  className="mt-0.5 size-4 shrink-0 text-[var(--action)]"
                  aria-hidden="true"
                />
                {feature}
              </li>
            ))}
          </ul>
          <div className="mt-auto pt-6">
            <Button asChild variant="secondary">
              <Link to="/signup">Create an account</Link>
            </Button>
          </div>
        </article>

        <article className="surface flex flex-col rounded-2xl border border-[var(--action)] p-6">
          <h2 className="text-lg font-semibold tracking-tight">Business</h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            One paid plan per organization. Activated after a short review — no
            usage metering, no overage.
          </p>
          <ul className="mt-5 grid gap-3 text-sm">
            {BUSINESS_FEATURES.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <Check
                  className="mt-0.5 size-4 shrink-0 text-[var(--action)]"
                  aria-hidden="true"
                />
                {feature}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-xs text-[var(--muted-foreground)]">
            Cloud and the AI platform are separate SYLVIQ products with their
            own pricing and sites; this plan covers the Business area only.
          </p>
          <div className="mt-auto pt-6">
            <Button asChild>
              <a href="mailto:support@sylviq.io?subject=SYLVIQ%20Business%20plan">
                Request Business
              </a>
            </Button>
          </div>
        </article>
      </section>
    </SiteLayout>
  );
}
